import Popper from 'popper.js';

import SvgIcon from 'CoreComponents/svgIcon';

// Default class names for select input states
const CLASS_NAME = {
  BASE_WRAPPER: 'select',
  SELECT_STYLED: 'select--styled',
  SELECT_PLACEHOLDER: 'select--placeholder',
  SELECT_OPTIONS: 'select__options',
  SELECT_OPEN: 'select--open',
  DISABLED: 'select--disabled',
  HIDDEN: 'hidden-xl-down'
};

// Default configs for popper
const CONFIG = {
  placement: 'bottom-end'
};

// Default select input selector
export const DEFAULT_SELECTOR = '.select-input';

export const DEFAULT_OPTIONS = {
  className: CLASS_NAME,
  config: CONFIG,
  selector: DEFAULT_SELECTOR,
  addDropdownIcon: true,
  addPopper: true,
  disabled: false,
  isSearchable: false
};

class SelectInput {
  /**
   * Appends `node` after `referenceNode`
   *
   * @param {Element} node - HTML node to be added in the DOM
   * @param {Element} referenceNode - HTML node after which `node` will be added.
   * */
  static appendAfter(node, referenceNode) {
    referenceNode.parentNode.insertBefore(node, referenceNode.nextSibling);
  }

  /**
   * Creates the search input inside the option list.
   * */
  static createSearchField() {
    let searchableInput = document.createElement('input');
    searchableInput.setAttribute('type', 'search');

    // FIXME: allow modifications for placeholders
    searchableInput.setAttribute('placeholder', 'Search event types');

    let listItem = document.createElement('li');

    listItem.appendChild(searchableInput);
    return (listItem);
  }

  /**
   * Initializer
   *
   * @param {Element} elementRef - HTML node, to add selectInput properties on.
   * @param {Object} options - Configuration options, defaults to `DEFAULT_OPTIONS`.
   * */
  constructor(elementRef, options = DEFAULT_OPTIONS) {
    // Initialise attributes

    // Root selectInput container
    this.elementRef = elementRef;
    this.options = options;
    if (this.elementRef.disabled) {
      this.options.disabled = true;
    }

    this.selectOptions = this.elementRef.querySelectorAll('option');

    // Object to hold all the className of the selectInput
    this.className = options.className || CLASS_NAME;
    this.config = options.config || CONFIG;
    this.isSearchable = options.isSearchable;
    this.disabled = (options.disabled === undefined) ? DEFAULT_OPTIONS['disabled'] : options.disabled;
    this.addPopper = (options.addPopper === undefined) ? DEFAULT_OPTIONS['addPopper'] : options.addPopper;
    this.addDropdownIcon = (options.addDropdownIcon === undefined) ? DEFAULT_OPTIONS['addDropdownIcon'] : options.addDropdownIcon;

    // Bind functions
    this.selectHandler = this.selectHandler.bind(this);
    this.close = this.close.bind(this);
    this.toggle = this.toggle.bind(this);

    this._setup();
  }

  /**
   * Disable the selectInput.
   *  1) Disables click actions by enabling `this.disabled`.
   *  2) Closes the option lists in case its open.
   *  3) Adds `DISABLED` class to the base wrapper.
   * */
  disable() {
    this.disabled = true;
    this.close();
    this.baseWrapper.classList.add(`${this.className['DISABLED']}`);
  }

  /**
   * Enable the selectInput.
   *  1) Enables click actions by disabling `this.disabled`.
   *  2) Removes `DISABLED` class from the base wrapper.
   * */
  enable() {
    this.disabled = false;
    this.baseWrapper.classList.remove(`${this.className['DISABLED']}`);
  }

  // Event Handlers

  /**
   * Event handler to select the option.
   *
   * @param {Event} event - click event on the option.
   * @param {Element} option - HTML node clicked
   * */
  selectHandler(event, option) {
    event.preventDefault();
    event.stopPropagation();

    this.elementRef.value = option.getAttribute('rel');

    this.styledSelect.classList.remove(this.className['SELECT_PLACEHOLDER']);

    // Update the text and icon inside the dropdown
    this.styledSelect.querySelector('div').textContent = option.textContent;

    // Change event on select is only triggered when the user commits the change explicitly
    // (e.g. by selecting a value from a <select>'s dropdown with a mouse click
    // https://developer.mozilla.org/en-US/docs/Web/Events/change#Description
    //
    // Create and dispatch a manual change event
    let changeEvent = new Event('change');
    this.elementRef.dispatchEvent(changeEvent);

    this.toggle(event);
  }

  /**
   * Event handler to toggle the selectInput.
   *
   * @param {Event} event - click event on the selectInput `this.elementRef`.
   * */
  toggle(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.disabled) { return; }

    this.baseWrapper.classList.toggle(`${this.className['SELECT_OPEN']}`);
    this.addPopper && this.popper.update();
  }

  /**
   * Closes the option list.
   * */
  close() {
    this.baseWrapper.classList.remove(`${this.className['SELECT_OPEN']}`);
  }

  /**
   * Resets the select input. Deletes the current DOM element and re-renders them.
   * */
  reset() {
    this.destroy();
    this.elementRef.selectedOptions[0].selected = false;

    return new SelectInput(this.elementRef, this.options);
  }

  /**
   * Destroy all the associated DOM elements.
   * */
  destroy() {
    this.baseWrapper.remove();
  }

  // private

  /**
   * Set-up the selectInput.
   *  1) Prepares all the DOM Nodes needed for the selectInput.
   *  2) If `this.addPopper` is true, it initializes `Popper` with the optionList.
   * */
  _setup() {
    this.baseWrapper = this._createBaseWrapper();
    this.styledSelect = this._createStyledSelect();
    this.optionList = this._createStyledOptionList();

    this.baseWrapper.appendChild(this.styledSelect);
    this.baseWrapper.appendChild(this.optionList);

    if (this.addPopper) {
      this.popper = new Popper(this.styledSelect, this.optionList, this.config);
    }

    this._addEventListeners();
  }

  /**
   * Creates the root container of the selectInput.
   *
   * @return {Element} - HTML node, root container of the selectInput.
   * */
  _createBaseWrapper() {
    let baseWrapper = document.createElement('div');
    baseWrapper.setAttribute('class', this.className['BASE_WRAPPER']);

    this._baseWrapperAttributes().forEach(attribute => {
      baseWrapper.setAttribute(attribute.name, attribute.nodeValue);
    });

    this.constructor.appendAfter(baseWrapper, this.elementRef);

    return baseWrapper;
  }

  /**
   * Creates the visible selected option.
   *
   * @return {Element} - HTML node, visible selected option.
   * */
  _createStyledSelect() {
    let styledSelect = document.createElement('div');
    styledSelect.setAttribute('class', this.className['SELECT_STYLED']);
    styledSelect.appendChild(document.createElement('div'));

    let selectedItem = this.elementRef.selectedOptions[0];

    if (selectedItem && selectedItem.innerText) {
      styledSelect.querySelector('div').textContent = this.elementRef.selectedOptions[0].innerText;
    } else {
      styledSelect.classList.add(this.className['SELECT_PLACEHOLDER']);
      styledSelect.querySelector('div').textContent = this.elementRef.dataset.placeholder;
    }

    if (this.addDropdownIcon) {
      let selectIcon = SvgIcon({ name: 'icon-dropdown', width: 10, height: 10 });
      styledSelect.appendChild(selectIcon);
    }

    return styledSelect;
  }

  /**
   * Creates the styled option list.
   *
   * @return {Element} - HTML node, styled option list.
   * */
  _createStyledOptionList() {
    let optionList = document.createElement('ul');
    optionList.setAttribute('class', this.className['SELECT_OPTIONS']);

    if (this.isSearchable) {
      let searchField = this.constructor.createSearchField();
      optionList.appendChild(searchField);
    }

    for (let option of this.selectOptions) {
      let listItem = document.createElement('li');

      listItem.innerHTML = option.innerText;
      listItem.setAttribute('rel', option.value);

      option.classList.value && listItem.classList.add(option.classList.value);

      optionList.appendChild(listItem);
    }

    return optionList;
  }

  /**
   * Filter out the attributes to copy/clone from the original element to the custom select.
   *
   * @return {Array} - the list of attributes to copy
   * */
  _baseWrapperAttributes() {
    const reservedAttributes = ['id', 'class', 'name'];

    return this.elementRef.attributes
      .filter(attribute => !reservedAttributes.includes(attribute.name));
  }

  /**
   * Adds all the required event listeners.
   * */
  _addEventListeners() {
    this.styledSelect.addEventListener('click', this.toggle);

    for (let option of this.optionList.querySelectorAll('li[rel]')) {
      option.addEventListener('click', event => { this.selectHandler(event, option); });
    }

    document.addEventListener('click', this.close);
  }
}

export default SelectInput;



// WEBPACK FOOTER //
// ./app/assets/javascripts/components/selectInput/index.js